import { BankCode, BonusType } from ".";

export interface User {
  uid: string;
  email: string;
  name: string;
}

export interface Response<T = any> {
  status: string;
  code: number;
  serviceCode: string;
  serviceMessage: string;
  data: T;
}

export interface Pagination<T> {
  page: number;
  pageSize: number;
  total: number;
  results: T[];
}

export type TransactionType = "DEPOSIT" | "WITHDRAW" | "REFUND";

export type DepositTransactionStatus =
  | "PENDING"
  | "REJECTED"
  | "COMPLETED"
  | "WAITING_TRANSFER"
  | "TRANSFER_BACK"
  | "CANCELLED";

export type WithdrawTransactionStatus = "PENDING" | "REJECTED" | "COMPLETED";
export type MatchingState = "AUTO" | "MANUAL";
export type AdjustType = "WRONG_SLIP" | "WRONG_USER" | "VOIDED_WRONG_USER" | "VOIDED_WRONG_SLIP";

export interface EditTransaction {
  createdAt: string;
  updatedAt: string;
  uid: string;
  transactionUid: string;
  editingByUid: string;
}

export interface Transaction {
  uid: string;
  adjustType: AdjustType;
  type: TransactionType;
  fromBankCode: BankCode | null;
  fromBankAccountName: string | null;
  fromBankAccountNumber: string | null;
  toBankCode: BankCode | null;
  toBankAccountName: string | null;
  toBankAccountNumber: string | null;
  amount: number;
  isBonus: boolean;
  coinAmount: number;
  transferredAt: string | null;
  createdAt: string;
  updatedAt: string;
  editingBy: User | null;
  editTransaction: EditTransaction | undefined;
  editedTime: string | null;
  note: string | null;
  latestActionAt: string;
  isSent: boolean; // NOTE : it means "it is sending"
}

export interface DepositTransaction extends Transaction {
  fromBankCode: BankCode;
  fromBankAccountName: string | null;
  fromBankAccountNumber: string;
  toBankCode: BankCode;
  toBankAccountName: string;
  toBankAccountNumber: string;
  transferredAt: string;
  status: DepositTransactionStatus;
  bonusType?: BonusType;
  matchingState: MatchingState;
  customerUid: string | null;
  customerCode: string | null;
  refund: Omit<RefundTransaction, "transactionRef"> | null;
}

export type WithdrawTransferType = "AFFILIATE" | "TIP" | "RAIN" | "PIGHUNT";

export interface WithdrawTransaction extends Transaction {
  status: WithdrawTransactionStatus;
  matchingState: MatchingState;
  customerUid: string | null;
  customerCode: string | null;
  customerPhoneNumber: string | null;
  // null / undefined = main wallet; "AFFILIATE" = withdrawn from the affiliate wallet.
  transferType?: WithdrawTransferType | null;
  // Optional: only withdrawals routed through a provider carry a payout
  // reference, and rows created before the column existed have none.
  refId?: string | null;
  // Set only on rows split off from another withdrawal (e.g. the remaining
  // amount after a partial auto-withdraw); holds the parent's refId, so
  // standalone withdrawals have none.
  parentRefId?: string | null;
}

export interface RefundTransaction extends Transaction {
  transactionRef: Omit<DepositTransaction, "refund">;
  status: DepositTransactionStatus | WithdrawTransactionStatus;
}

export enum PartyBrand {
  PIGSPIN = "PIGSPIN",
  PIGBET = "PIGBET",
  PIGSPORT = "PIGSPORT",
}

export enum GameStatus {
  active = "Active",
  inactive = "Inactive",
}

export type GameStatusTypes = GameStatus.active | GameStatus.inactive;

export interface Games {
  noIndex?: number;
  gameId: string;
  gameName: string;
  gameCode: string;
  logoUrl?: string;
  coverUrl?: string;
  status: GameStatusTypes;
  order?: number;
  isPopular?: boolean;
  isNew?: boolean;
  isNewCard?: boolean;
  isHighlight?: boolean;
  isLobbyBanner?: boolean;
  refType?: string;
  rowIndex?: number;
  featureOption?: boolean;
  reelType?: string;
  wildType?: string;
  volatility?: string;
  maxWin?: number;
  rtp?: number;
  actualRtp?: string | null;
  actualRtpType?: string;
  createdAt?: string;
}

export interface GameBrands {
  uid: string;
  name: string;
  providerName: string;
  providerUid: string;
  logo?: string;
  party: string;
  isActive: boolean;
  isUnderConstruction?: boolean;
  isHot?: boolean;
  isComingSoon?: boolean;
  updatedAt?: string;
  createdAt?: string;
  lastIsActiveUpdated?: string;
  adminName?: string;
  totalGame?: number;
}

export interface UpdateGameResponse {
  status?: string;
  order?: number;
  is_popular?: boolean;
  is_new?: boolean;
}

export interface UpdateGameBrandResponse {
  partyBrand: PartyBrand;
  isProvider?: boolean;
}

export interface TransactionLog {
  createdAt: string;
  created_at: string;
  remark: string;
  uid: string;
}

export interface DepositTransactionWithLogs extends DepositTransaction {
  logs: TransactionLog[];
}

export interface WithdrawTransactionWithLogs extends WithdrawTransaction {
  logs: TransactionLog[];
}

export interface RefundTransactionWithLogs extends RefundTransaction {
  logs: TransactionLog[];
}

export type PolymorphicTransaction = DepositTransaction | WithdrawTransaction | RefundTransaction;

export type PolymorphicTransactionWithLogs =
  | DepositTransactionWithLogs
  | WithdrawTransactionWithLogs
  | RefundTransactionWithLogs;

export function isDepositTransaction(
  transaction: PolymorphicTransaction,
): transaction is DepositTransaction {
  return transaction.type === "DEPOSIT";
}

export function isWithdrawTransaction(
  transaction: PolymorphicTransaction,
): transaction is WithdrawTransaction {
  return transaction.type === "WITHDRAW";
}

export function isRefundTransaction(
  transaction: PolymorphicTransaction,
): transaction is RefundTransaction {
  return transaction.type === "REFUND";
}

export enum CoinWalletStatus {
  ACTIVE = "active",
  CONVERTED = "converted",
  USED = "used",
  REVOKED = "revoked",
  CANCELLED = "canceled",
  EXPIRED = "expired",
}

// Product codes a bonus applies to (API `on_products`).
// PIG_SPIN→SLOT, PIG_CASINO→BET, PIG_SPORT→SPORT.
export enum ProductCode {
  PIG_SPIN = "PIG_SPIN",
  PIG_CASINO = "PIG_CASINO",
  PIG_SPORT = "PIG_SPORT",
}

export interface CoinWallet {
  cashWalletTransaction: {
    transactionAmount: number;
  };
  createdAt: string;
  currentBalance: number;
  currentTurnover: number;
  expireAt: string;
  initialBalance: number;
  isActive: boolean;
  isConvertedToCash: boolean;
  name: string;
  promotionUid: string | null;
  status: CoinWalletStatus;
  totalTurnover: number;
  turnOverRatio: number;
  convertedToCashAmount: number;
  convertLimitAmount: number;
  uid: string;
  updatedAt: string;
  // Products this bonus applies to (API `on_products`); drives the SLOT/BET/SPORT
  // channel columns. Empty/undefined = applies to all products.
  onProducts?: ProductCode[];
}

export enum CoinWalletLogActionType {
  UPDATE = "UPDATE",
  REVOKE = "REVOKE",
}

export interface CoinWalletLog {
  uid: string;
  actionType: CoinWalletLogActionType;
  actionBy: User;
  createdAt: string;
  updatedAt: string;
}

export interface CoinWalletCreate {
  customerCode: string;
  expireAt: string;
  initialBalance: number;
  name: string;
  promotionUid: string | null;
  turnOverRatio: number;
  initial: boolean;
  isForceWithdraw: boolean;
  convertLimitAmount: number;
  convertLimitRatio: number;
}

export interface CoinWalletUpdate {
  currentBalance: number;
  expireAt: string;
  initialBalance: number;
  isActive: boolean;
  name: string;
  promotionUid: string | null;
  status: CoinWalletStatus;
  totalTurnover: number;
  turnOverRatio: number;
  walletId: string;
  isForceWithdraw: boolean;
  convertLimitRatio: number;
}

export interface Promotion {
  beginAt: string | null;
  bonusRatio: number;
  code: string;
  convertLimitAmount: number;
  convertLimitRatio: number;
  durationInHours: number;
  endAt: string | null;
  initialCoinAmount: number;
  isActive: boolean;
  isForceWithdraw: boolean;
  maximumBonus: number;
  name: string;
  order: number;
  turnoverRatio: number;
  type: string;
  uid: string;
}

export enum CustomerLogType {
  CREDIT = "Credit",
  DEBIT = "Debit",
  GAME = "Game",
  BONUS = "Bonus",
  VOID = "Void",
  CONVERT = "CONVERT",
  TICKET = "TICKET",
  TOURNAMENT = "TOURNAMENT",
  TIP_CREDIT = "TIP_CREDIT",
  TIP_DEBIT = "TIP_DEBIT",
  RAIN_CREDIT = "RAIN_CREDIT",
  RAIN_DEBIT = "RAIN_DEBIT",
  FREE_SPIN = "FREE_SPIN",
  PG_FREE_SPIN = "PG_FREE_SPIN",
  CASINO_COMMISSION_01_PERCENT = "CASINO_COMMISSION_01_PERCENT",
  CASINO_COMMISSION_LOSING_STREAK = "CASINO_COMMISSION_LOSING_STREAK",
  PIGHUNT = "PIGHUNT",
  CASINO_TIPS = "CASINO_TIPS",
}

export interface CustomerLog {
  afterBalance: number;
  amount: number;
  isBonus: boolean;
  bonusType?: BonusType;
  beforeBalance: number;
  betTransactionRefId: string;
  cashBalance: number;
  coinBonusAmount: number | null;
  coinBonusBalance: number;
  createdAt: string;
  createdByUid: string | null;
  deletedAt: string | null;
  gameName: string;
  gameProvider?: string | null;
  parentBetId?: string | null;
  // Sport bills only (wallet derives them from the bill's own rows); null for other providers.
  betAt?: string | null;
  // Whether the provider says the spin was a bought feature; null when it doesn't say.
  isFeatureBuy?: boolean | null;
  settledAt?: string | null;
  sportDetail?: SportBetDetail | null;
  payoutAmount: number;
  payoutTransactionRefId: string;
  transactionType: CustomerLogType;
  uid: string;
  updatedAt: string;
  updatedByUid: string | null;
  userCode: string;
  walletTransactionRefUid: string;
  turnOverAmount: number;
}

export type SportBetType = "single" | "step" | "parlay";

export interface SportBetLeg {
  sport: string | null;
  league: string | null;
  matchTime: string | null;
  home: string | null;
  away: string | null;
  selection: string | null;
  handicap: string | null;
  price: string | null;
  market: string | null;
  torSide: "home" | "away" | null;
  isLive: boolean;
  score: string | null;
}

export interface SportBetDetail {
  betType: SportBetType;
  sportName: string | null;
  oddsType: string | null;
  legs: SportBetLeg[];
}

/** One play session of a game, or one sport bill (betTransactionRefId set), from wallet game-sessions. */
export interface GameSession {
  // UTC day the part was cached under; sessions crossing midnight carry their first day.
  cacheDay: string;
  gameProvider: string;
  gameName: string | null;
  betTransactionRefId: string | null;
  // Sport bills only: lets the rounds request use the parent_bet_id index.
  parentBetId: string | null;
  startedAt: string;
  lastRoundAt: string;
  endedAt: string | null;
  roundCount: number;
  betCash: number;
  betCoin: number;
  payout: number;
  // null while a sport bill is pending.
  net: number | null;
  freeSpinCount: number;
  featureBuyCount: number;
  pendingCount: number;
  cancelledCount: number;
  gapMinutes: number;
  sportDetail?: SportBetDetail | null;
}

export interface GameSessionSummary {
  sessionCount: number;
  roundCount: number;
  betTotal: number;
  payoutTotal: number;
  netTotal: number;
  pendingCount: number;
  cancelledCount: number;
}

// "preparing": settled days of the range are being cached in the background; poll until "ready".
export type GameSessionStatus = "ready" | "preparing" | "failed";

export interface GameSessionPage {
  status: GameSessionStatus;
  preparedDays?: number;
  totalDays?: number;
  total: number;
  results: GameSession[];
  summary: GameSessionSummary | null;
}

export interface CustomerGameStat {
  gameName: string;
  count: number;
}


export interface CoreQrReportItem {
  createdAt: string;
  updatedAt: string;
  customerCode: string;
  amount: number;
  provider: string;
  status: string;
  refId: string;
  providerRefId: string;
}

export enum BankTransactionType {
  API = "API",
  SMS = "SMS",
  IBANK = "IBANK",
  MBANK = "MBANK",
  MANUAL = "MANUAL",
}

export enum Channel {
  KPlus = "K PLUS",
  IBankMBank = "Internet/Mobile Across Banks",
  MakeByKBank = "MAKE by KBank",
  Empty = "",
  LineBk = "LINE BK",
}

export enum QuestType {
  GAME = "game",
  DEPOSIT = "deposit",
  LOGIN = "login",
  ANY = "any",
}

export enum QuestLevel {
  EASY = "easy",
  MEDIUM = "medium",
  HARD = "hard",
}

export enum GameRefType {
  PG_GAME = "PG",
  EVO_GAME = "EVO",
  JOKER_GAME = "JOKER",
  PNG_GAME = "PNG",
}

export interface BankReportItem {
  reportId: string;
  transactionDate: string;
  customerBankCode: BankCode | string | null;
  customerAccountName: string | null;
  customerAccountNumber: string | null;
  amount: number;
  pigBankCode: BankCode | null;
  pigAccountName: string | null;
  pigAccountNumber: string | null;
  bankPromptpayNo: string | null;
  channel: Channel;
  createdAt: string;
  transactionChannel: string;
  transactionType: BankTransactionType;
}

export interface BankReportItemTable extends BankReportItem {
  transactionTime: string;
  createdDate: string;
  createdTime: string;
}

export interface TicketManagementAPI {
  createdAt: string;
  questName: string | null;
  gameName: string | null;
  ticketType: string;
  ticketBefore: number;
  ticketUsed: number;
  ticketAfter: number;
  bigwintransactionUid: string;
  imageUrl: string;
  totalTicket: number;
}

export interface QuestManagementAPI {
  questUid: string;
  questName: string;
  questType: QuestType;
  questLevel: QuestLevel;
  reward: string;
  children: [
    {
      questUid: string | null;
      questName: string;
      gameName: string;
      gameRefType: string;
      questTypeEdit: string;
      questType: QuestType;
      questLevel: QuestLevel;
      reward: number;
    },
  ];
}

export interface WeeklyQuestManagementAPI {
  uid: string;
  gameRefType: string;
  gameId: string;
  gameName: string;
  questCode: string;
  questName: string;
  reward: number;
  questRules: QuestLevel;
}

export interface VipHistoryAPI<T> {
  turnOver: number;
  vipLevel: number;
  result: T[];
}

export interface VipHistoryResult {
  customerCode: string;
  tier: string;
  level: number;
  receivedAt: string;
  type: string;
  detail: string;
}
